import {  Contact, User, Location, CalendarEvent, Message, CampaignStatus, Calendar, Link, countryLocale } from "../models";
import {CustomValue,FieldType} from "../models/api"
import { CalendarService } from "../models";
import { get } from "lodash";
import * as moment from "moment-timezone";
import * as libphonenumber from 'google-libphonenumber';
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
import handlebars from 'handlebars';
import store from '../store';

export async function getValues(
  locationId: string,
  params: { [key: string]: any }
) {
  const templateParams = {
    custom_values: {}
  };
  try {
    if (locationId) {
      const values = await CustomValue.list(locationId);
      values.forEach(v => {
        if (!v.value || !v.fieldKey) return;
        // extract field key from the templateString returned by the api
        // {{ custom_values.test_custom_value }} -> test_custom_value
        const fieldKey = v.fieldKey.replace(/[{}]/g, "").trim().split(".")[1];
        templateParams.custom_values[fieldKey] = new handlebars.SafeString(
          handlebars.compile(v.value)(params)
        );

        const regex = /{{[\w\s]+\.\w+/m;

        // Check if is a link
        if (v.value.startsWith('http') && regex.test(v.value)) {
          templateParams.custom_values[fieldKey] = new handlebars.SafeString(encodeURI(templateParams.custom_values[fieldKey]))
        }
      });
    }
  } catch (err) {
    console.error(err);
  }
  return templateParams;
}


export function formatName(str: string) {
	if (!str) return '';

  var words = [];
  str.split(' ').map((value) => {
    if (value.indexOf('-') > -1) {
      var chunkstr = value.split('-').map((val) => {
          return val.slice(0, 1).toUpperCase() + val.slice(1);
      });
      words.push(chunkstr.join('-'));
    } else {
        words.push(value.slice(0, 1).toUpperCase() + value.slice(1));
    }
  });
  return words.join(' ')
}

export async function getParams(
  contact: Contact,
  extras: {
    user?: User;
    appointment?: CalendarEvent;
    message?: Message;
    campaignStatus?: CampaignStatus;
    calendar?: Calendar;
    calendarService?: CalendarService;
    timezone?: string;
    form?: { [key: string]: any };
    location?: Location;
    shortenAnyLinks?: boolean;
    locationId: string;
  }
): Promise<{ [key: string]: any }> {
  const templateParams: { [key: string]: any } = {
    user: {},
    contact: {},
    appointment: {},
    calendar: {},
    calendarService: {},
    form: {},
    location: {},
    trigger_link: {},
    custom_values: {},
    campaign: {},
    message: {}
  };

  const country =
    extras.location && extras.location.country ? extras.location.country : 'US';

  let whitelabelUrl;

  let contactPhone = contact.phone || '';
  try {
    const phoneNumber = phoneUtil.parse(contactPhone);
    if (phoneUtil.getRegionCodeForNumber(phoneNumber) === country) {
      contactPhone = phoneUtil.format(
        phoneNumber,
        libphonenumber.PhoneNumberFormat.NATIONAL
      );
    } else {
      contactPhone = phoneUtil.format(
        phoneNumber,
        libphonenumber.PhoneNumberFormat.INTERNATIONAL
      );
    }
  } catch (err) {}
  templateParams.contact = {
    first_name: new handlebars.SafeString(
      formatName(contact.firstName || '')
    ),
    last_name: new handlebars.SafeString(
      formatName(contact.lastName || '')
    ),
    name: new handlebars.SafeString(formatName(contact.name || '')),
    phone: new handlebars.SafeString(contactPhone),
    phone_raw: new handlebars.SafeString(contact.phone || ''),
    email: new handlebars.SafeString(formatName(contact.email || '')),
    city: new handlebars.SafeString(formatName(contact.city || '')),
    company_name: new handlebars.SafeString(
      formatName(contact.companyName || '')
    ),
    full_address: new handlebars.SafeString(contact.fullAddressLine),
    address1: new handlebars.SafeString(contact.address1),
    state: new handlebars.SafeString(contact.state),
    country: new handlebars.SafeString(contact.country),
    postal_code: new handlebars.SafeString(contact.postalCode),
    website: new handlebars.SafeString(contact.website),
    source: new handlebars.SafeString(contact.source),
    id: new handlebars.SafeString(contact.id)
  };

  try {
    const customFields = await this.$store.dispatch('locationCustomFields/getCustomFields',{locationId:extras.locationId})

    if (contact.customFields.length > 0) {
      contact.customFields.forEach(field => {
        const customField = customFields.find(v => v.id === field.id);
        if (!customField) return;

        const type = customField.dataType || '';
        let fieldValue: any = field.field_value;

        // Field Type check
        if (
          FieldType[type] === FieldType.NUMERICAL &&
          typeof field.field_value === 'string'
        ) {
          fieldValue = +field.field_value.replace(/[^0-9\.]/g, '');
        } else if (FieldType[type] === FieldType.MONETORY) {
          const localeCurrency = countryLocale.find(v => v.code === country);
          fieldValue =
            localeCurrency.currency.symbol +
            parseFloat(field.field_value)
              .toFixed(2)
              .toString();
        } else if (FieldType[type] === 'Float') {
          fieldValue = parseFloat(Number(field.field_value).toFixed(2));
        } else if (
          FieldType[type] === FieldType.CHECKBOX ||
          FieldType[type] === FieldType.MULTIPLE_OPTIONS
        ) {
          fieldValue = Array.isArray(field.field_value)
            ? field.field_value.join(', ')
            : field.field_value;
        }

        templateParams.contact[field.id] = fieldValue;
        templateParams.contact[customField.fieldKey.substr(8)] = fieldValue;
      });
    }
  } catch (err) {
    console.error(err);
  }

  if (extras.campaignStatus) {
    const eventTime = extras.timezone
      ? moment(extras.campaignStatus.eventTime).tz(extras.timezone)
      : moment(extras.campaignStatus.eventTime);
    templateParams.campaign.event_date = new handlebars.SafeString(
      eventTime.format('MMM Do YYYY')
    );
    templateParams.campaign.event_time = new handlebars.SafeString(
      eventTime.format('h:mma')
    );
    templateParams.campaign.event_date_time = new handlebars.SafeString(
      eventTime.format('MMM Do YYYY, h:mma')
    );
  }

  if (extras.location) {
    let phone = extras.location.phone || '';
    try {
      const phoneNumber = phoneUtil.parse(phone);
      if (phoneUtil.getRegionCodeForNumber(phoneNumber) === country) {
        phone = phoneUtil.format(
          phoneNumber,
          libphonenumber.PhoneNumberFormat.NATIONAL
        );
      } else {
        phone = phoneUtil.format(
          phoneNumber,
          libphonenumber.PhoneNumberFormat.INTERNATIONAL
        );
      }
    } catch (err) {}
    templateParams.location = {
      name: new handlebars.SafeString(extras.location.name || ''),
      city: new handlebars.SafeString(extras.location.city || ''),
      state: new handlebars.SafeString(extras.location.state || ''),
      country: new handlebars.SafeString(extras.location.country || ''),
      address: new handlebars.SafeString(extras.location.address || ''),
      email: new handlebars.SafeString(extras.location.email || ''),
      phone: new handlebars.SafeString(phone),
      postal_code: new handlebars.SafeString(extras.location.postalCode || ''),
      full_address: new handlebars.SafeString(
        extras.location.fullAddressLine || ''
      ),
      website: new handlebars.SafeString(extras.location.website || ''),
      logo_url: new handlebars.SafeString(extras.location.logoUrl || ''),
      id: new handlebars.SafeString(extras.location.id)
    };
    templateParams.location_owner = {
      first_name: new handlebars.SafeString(extras.location?.prospectInfo?.first_name || ''),
      last_name: new handlebars.SafeString(extras.location?.prospectInfo?.last_name || ''),
      email: new handlebars.SafeString(extras.location?.prospectInfo?.email || ''),
    }
  }
  if (extras.user) {
    let phone = extras.user.phone || '';
    try {
      const phoneNumber = phoneUtil.parse(phone);
      if (phoneUtil.getRegionCodeForNumber(phoneNumber) === country) {
        phone = phoneUtil.format(
          phoneNumber,
          libphonenumber.PhoneNumberFormat.NATIONAL
        );
      } else {
        phone = phoneUtil.format(
          phoneNumber,
          libphonenumber.PhoneNumberFormat.INTERNATIONAL
        );
      }
    } catch (err) {}
    if (extras.user.userCalendar[extras.locationId] && !whitelabelUrl) {
      whitelabelUrl = await store.dispatch('company/getWhitelabelDomain');
    }

    let twilioPhone
    try {
      const twilioPhoneNumber = phoneUtil.parse(extras.user.twilioPhone[extras.locationId]);
      if (phoneUtil.getRegionCodeForNumber(twilioPhoneNumber) === country) {
        twilioPhone = phoneUtil.format(
          twilioPhoneNumber,
          libphonenumber.PhoneNumberFormat.NATIONAL
        );
      } else {
        twilioPhone = phoneUtil.format(
          twilioPhoneNumber,
          libphonenumber.PhoneNumberFormat.INTERNATIONAL
        );
      }
    } catch (error) { }

    templateParams.user = {
      first_name: new handlebars.SafeString(
        formatName(extras.user.firstName || '')
      ),
      first_name_lower_case: new handlebars.SafeString(extras.user.firstNameLowerCase || ''),
      last_name: new handlebars.SafeString(
        formatName(extras.user.lastName || '')
      ),
      last_name_lower_case: new handlebars.SafeString(extras.user.lastNameLowerCase || ''),
      name: new handlebars.SafeString(
        formatName(extras.user.fullName || '')
      ),

      phone: new handlebars.SafeString(formatName(phone)),
      email: new handlebars.SafeString(
        formatName(extras.user.email || '')
      ),
      email_signature: new handlebars.SafeString(
        extras.user.emailSignature || ''
      ),

      calendar_link: new handlebars.SafeString(
        extras.user.userCalendar[extras.locationId]
          ? whitelabelUrl +
            '/widget/booking?calendar=' +
            extras.user.userCalendar[extras.locationId]
          : ''
      ),
      twilio_phone_number: new handlebars.SafeString(twilioPhone || ''),
      twilio_phone_number_raw: new handlebars.SafeString(
        extras.user.twilioPhone[extras.locationId] || ''
      )
    };
  }

  if (extras.message) {
    const { type, body, direction, status } = extras.message;
    templateParams.message = { type, body, direction, status };
  }

  if (extras.appointment) {
    if (!whitelabelUrl) {
      whitelabelUrl = await store.dispatch('company/getWhitelabelDomain');
    }
    const startTime = extras.timezone
      ? moment(extras.appointment.startTime).tz(extras.timezone)
      : moment(extras.appointment.startTime);
    const endTime = extras.timezone
      ? moment(extras.appointment.endTime).tz(extras.timezone)
      : moment(extras.appointment.endTime);
    templateParams.appointment = {
      start_time: new handlebars.SafeString(
        startTime.format('dddd, MMM Do YYYY, h:mma')
      ),
      end_time: new handlebars.SafeString(endTime.format('dddd, MMM Do YYYY, h:mma')),
      only_start_date: new handlebars.SafeString(
        startTime.format('dddd, MMM Do YYYY')
      ),
      only_start_time: new handlebars.SafeString(startTime.format('h:mma')),
      only_end_date: new handlebars.SafeString(endTime.format('dddd, MMM Do YYYY')),
      only_end_time: new handlebars.SafeString(endTime.format('h:mma')),
      cancellation_link: new handlebars.SafeString(
        extras.appointment.getCancellationLink(whitelabelUrl)
      ),
      reschedule_link: new handlebars.SafeString(
        extras.appointment.getRescheduleLink(whitelabelUrl)
      ),
      notes: new handlebars.SafeString(extras.appointment.notes),
      meeting_location: new handlebars.SafeString(extras.appointment.address),
      add_to_google_calendar: new handlebars.SafeString(
        extras.appointment.getAddGoogleCalendarLink(whitelabelUrl)
      ),
      add_to_ical_outlook: new handlebars.SafeString(
        extras.appointment.getICSLink(whitelabelUrl)
      )
    };

    if(extras.appointment.assignedUserId) {

      let user = await User.getById(extras.appointment.assignedUserId)


    let twilioPhone
    try {
      const twilioPhoneNumber = phoneUtil.parse(user.twilioPhone[extras.locationId]);
      if (phoneUtil.getRegionCodeForNumber(twilioPhoneNumber) === country) {
        twilioPhone = phoneUtil.format(
          twilioPhoneNumber,
          libphonenumber.PhoneNumberFormat.NATIONAL
        );
      } else {
        twilioPhone = phoneUtil.format(
          twilioPhoneNumber,
          libphonenumber.PhoneNumberFormat.INTERNATIONAL
        );
      }
    } catch (error) { }

      const appoitmentUser = {
        first_name: new handlebars.SafeString(
          formatName(user.firstName || '')
        ),
        first_name_lower_case: new handlebars.SafeString(user.firstNameLowerCase || ''),
        last_name: new handlebars.SafeString(
          formatName(user.lastName || '')
        ),
        last_name_lower_case: new handlebars.SafeString(user.lastNameLowerCase || ''),
        name: new handlebars.SafeString(
          formatName(user.fullName || '')
        ),

        phone: new handlebars.SafeString(formatName(user.phone)),
        email: new handlebars.SafeString(
          formatName(user.email || '')
        ),
        email_signature: new handlebars.SafeString(
          user.emailSignature || ''
        ),

        calendar_link: new handlebars.SafeString(
          user.userCalendar[extras.locationId]
            ? whitelabelUrl +
              '/widget/booking?calendar=' +
              user.userCalendar[extras.locationId]
            : ''
        ),
        twilio_phone_number: new handlebars.SafeString(twilioPhone || ''),
        twilio_phone_number_raw: new handlebars.SafeString(
          user.twilioPhone[extras.locationId] || ''
        )
      };

      templateParams.appointment.user = appoitmentUser;
    }

  }
  if (extras.form) {
    templateParams.form = { ...extras.form };
    if (extras.form.start_time) {
      const startTime = extras.timezone
        ? moment(extras.form.start_time).tz(extras.timezone)
        : moment(extras.form.start_time);
      templateParams.form.start_date = new handlebars.SafeString(
        startTime.format('MMM Do YYYY')
      );
      templateParams.form.start_time = new handlebars.SafeString(
        startTime.format('h:mma')
      );
      templateParams.form.start_date_time = new handlebars.SafeString(
        startTime.format('MMM Do YYYY, h:mma')
      );
      if (!get(templateParams.appointment, 'start_time')) {
        templateParams.appointment.only_start_date = new handlebars.SafeString(
          startTime.format('MMM Do YYYY')
        );
        templateParams.appointment.only_start_time = new handlebars.SafeString(
          startTime.format('h:mma')
        );
        templateParams.appointment.start_time = new handlebars.SafeString(
          startTime.format('MMM Do YYYY, h:mma')
        );
      }
    }
    if (extras.form.end_time) {
      const endTime = extras.timezone
        ? moment(extras.form.end_time).tz(extras.timezone)
        : moment(extras.form.end_time);
      templateParams.form.end_date = new handlebars.SafeString(
        endTime.format('MMM Do YYYY')
      );
      templateParams.form.end_time = new handlebars.SafeString(
        endTime.format('h:mma')
      );
      templateParams.form.end_date_time = new handlebars.SafeString(
        endTime.format('MMM Do YYYY, h:mma')
      );
      if (!get(templateParams.appointment, 'end_time')) {
        templateParams.appointment.only_end_date = new handlebars.SafeString(
          endTime.format('MMM Do YYYY')
        );
        templateParams.appointment.only_end_time = new handlebars.SafeString(
          endTime.format('h:mma')
        );
        templateParams.appointment.end_time = new handlebars.SafeString(
          endTime.format('MMM Do YYYY, h:mma')
        );
      }
    }
  }
  if (extras.calendar) {
    templateParams.calendar = {
      name: new handlebars.SafeString(extras.calendar.name)
    };
  }

  if (extras.calendarService) {
    templateParams.calendarService = {
      name: new handlebars.SafeString(extras.calendarService.name)
    }
  }

  const now = extras.timezone ? moment().tz(extras.timezone) : moment().utc()
  templateParams.right_now = {
    second: new handlebars.SafeString(now.format('s')),
    minute: new handlebars.SafeString(now.format('m')),
    hour: new handlebars.SafeString(now.format('H')),
    hour_ampm: new handlebars.SafeString(now.format('h')),
    time: new handlebars.SafeString(now.format('H:m')),
    time_ampm: new handlebars.SafeString(now.format('h:m A')),
    ampm: new handlebars.SafeString(now.format('A')),
    day: new handlebars.SafeString(now.format('D')),
    day_of_week: new handlebars.SafeString(now.format('dddd')),
    month: new handlebars.SafeString(now.format('M')),
    month_english: new handlebars.SafeString(now.format('MMMM')),
    year: new handlebars.SafeString(now.format('Y')),
    middle_endian_date: new handlebars.SafeString(now.format('M/D/Y')),
    little_endian_date: new handlebars.SafeString(now.format('D/M/Y'))
  }

  return templateParams;
}

export async function getTemplateParams(
  contact: Contact,
  extras: {
    user?: User;
    appointment?: CalendarEvent;
    message?: Message;
    campaignStatus?: CampaignStatus;
    calendar?: Calendar;
    calendarService?: CalendarService;
    timezone?: string;
    form?: { [key: string]: any };
    location?: Location;
    shortenAnyLinks?: boolean;
  }
): Promise<{ [key: string]: any }> {
  let locationId;
  if (contact) locationId = contact.locationId;
  if (!locationId && extras.location) locationId = extras.location.id;
  const params = await getParams(contact, { ...extras, locationId });
  const values = await getValues(locationId, params);
  return {
    ...params,
    ...values
  };
}
